
'use client';

import { useEffect, useRef, useState, PropsWithChildren } from 'react';
import clsx from 'clsx';

interface ScrollRevealProps {
  className?: string;
  rootMargin?: string; // e.g. "0px 0px -10% 0px" to trigger earlier/later
  threshold?: number | number[]; // default 0.15
  // When true, children are wrapped in a div that has pointer-events preserved.
  preserve?: boolean;
  // optional animation classes (Tailwind utilities)
  enter?: string; // classes applied when in view
  exit?: string;  // classes applied when out of view
  transition?: string; // transition classes
}

export default function ScrollReveal({
  children,
  className,
  rootMargin = '0px 0px -12% 0px',
  threshold = 0.15,
  preserve = false,
  enter = 'opacity-100 translate-y-0',
  exit = 'opacity-0 translate-y-8',
  transition = 'transition-all duration-600 ease-out',
}: PropsWithChildren<ScrollRevealProps>) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const node = ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // setInView true if intersecting; false otherwise
          if (entry.isIntersecting) {
            setInView(true);
          } else {
            setInView(false);
          }
        });
      },
      { root: null, rootMargin, threshold }
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [rootMargin, threshold]);

  return (
    <div data-component-id="src/app/about/components/ScrollReveal.tsx:59:4" data-component-path="src/app/about/components/ScrollReveal.tsx" data-component-line="59" data-component-file="ScrollReveal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      ref={ref}
      className={clsx(
        transition,
        inView ? enter : exit,
        className
      )}
      // allow children to keep pointer events normally
      style={{ willChange: 'transform, opacity' }}
    >
      {preserve ? <div data-component-id="src/app/about/components/ScrollReveal.tsx:69:18" data-component-path="src/app/about/components/ScrollReveal.tsx" data-component-line="69" data-component-file="ScrollReveal.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">{children}</div> : children}
    </div>
  );
}
